import { useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

export const CODE_LENGTH = 4

const CELL_CLASS =
  "h-[72px] w-16 rounded-[14px] border-[1.5px] border-line-2 bg-card text-center font-display text-[30px] font-bold shadow-none caret-primary focus-visible:border-foreground focus-visible:ring-[3px] focus-visible:ring-foreground/[0.07] md:text-[30px] max-[560px]:h-16 max-[560px]:w-0 max-[560px]:min-w-0 max-[560px]:flex-1 max-[560px]:text-[26px]"

// Four one-digit cells that assemble the one hidden `name` field the server
// reads (design/spec/auth/onboarding.html `.code`). Typing advances, backspace
// on an empty cell steps back, and a paste or a one-time-code autofill of the
// whole code fills every cell from the one it landed in. `onChange` receives
// the assembled code so the step can enable its button at four digits.
// `digitLabel(position, length)` names each cell; "Digit N" by default.
export default function CodeInput({ name, invalid = false, onChange, digitLabel = (position) => `Digit ${position}` }) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""))
  const cells = useRef([])

  function commit(next, focus) {
    setDigits(next)
    onChange?.(next.join(""))
    cells.current[focus]?.focus()
  }

  function handleChange(index, raw) {
    const value = raw.replace(/\D/g, "")
    const next = [...digits]
    if (value.length <= 1) {
      next[index] = value
      commit(next, value && index < CODE_LENGTH - 1 ? index + 1 : index)
      return
    }
    value
      .split("")
      .slice(0, CODE_LENGTH - index)
      .forEach((digit, offset) => {
        next[index + offset] = digit
      })
    commit(next, Math.min(CODE_LENGTH - 1, index + value.length))
  }

  function handleKeyDown(index, event) {
    if (event.key !== "Backspace" || digits[index] || index === 0) return
    event.preventDefault()
    const next = [...digits]
    next[index - 1] = ""
    commit(next, index - 1)
  }

  return (
    <div className="flex gap-3 max-[560px]:gap-2">
      <input type="hidden" name={name} value={digits.join("")} />
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(el) => {
            cells.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={CODE_LENGTH}
          autoComplete={index === 0 ? "one-time-code" : "off"}
          autoFocus={index === 0}
          aria-label={digitLabel(index + 1, CODE_LENGTH)}
          aria-invalid={invalid || undefined}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          className={cn(CELL_CLASS, invalid && "border-primary")}
        />
      ))}
    </div>
  )
}
